import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
} from "@mui/material";
import { Close, Assessment } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import api from "../api";

const PreliminaryResultsModal = ({
    open,
    onClose,
    surveyId = null,
    title = "Generate Preliminary Report",
}) => {
    const intl = useIntl();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [jobResult, setJobResult] = useState(null);

    const handleClose = () => {
        setConfirmDialogOpen(false);
        setError(null);
        setJobResult(null);
        onClose();
    };

    const handleGenerateReport = async () => {
        setConfirmDialogOpen(false);
        setGenerating(true);
        setError(null);
        try {
            const result = await api.generatePreliminaryReport(surveyId);
            setJobResult(result);
        } catch (err) {
            setError(err.message || intl.formatMessage({ id: "preliminaryResults.error_generic", defaultMessage: "Failed to generate report. Please try again." }));
        } finally {
            setGenerating(false);
        }
    };

    return (
        <>
            {/* Main dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                sx={{
                    "& .MuiDialog-paper": {
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        background: "linear-gradient(135deg, #00438E 0%, #4A87C8 100%)",
                        color: "white",
                        px: 4,
                        py: 3,
                        position: "relative",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: "linear-gradient(90deg, #FFD700, #FFA000)",
                        },
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Assessment sx={{ fontSize: "1.8rem" }} />
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {title}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="large"
                            sx={{
                                color: "white",
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                                borderRadius: "50%",
                            }}
                        >
                            <Close sx={{ fontSize: "1.2rem" }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 4 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {generating ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 2 }}>
                            <CircularProgress sx={{ color: "#00438E" }} />
                            <Typography variant="body2" color="text.secondary">
                                <FormattedMessage id="preliminaryResults.generating" defaultMessage="Triggering report generation..." />
                            </Typography>
                        </Box>
                    ) : jobResult ? (
                        <Box
                            sx={{
                                p: 3,
                                background: "rgba(46, 125, 50, 0.06)",
                                border: "1px solid rgba(46, 125, 50, 0.3)",
                                borderRadius: "12px",
                                textAlign: "center",
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32", mb: 1 }}>
                                <FormattedMessage id="preliminaryResults.success_title" defaultMessage="Report generation started!" />
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {jobResult.message}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: "center", py: 3 }}>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                <FormattedMessage
                                    id="preliminaryResults.description"
                                    defaultMessage="Generate a preliminary report for this assessment based on the responses collected so far."
                                />
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Assessment />}
                                onClick={() => setConfirmDialogOpen(true)}
                                sx={{
                                    background: "linear-gradient(135deg, #00438E, #4A87C8)",
                                    color: "white",
                                    borderRadius: "12px",
                                    px: 4,
                                    py: 1.5,
                                    textTransform: "none",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    boxShadow: "0 4px 16px rgba(0, 67, 142, 0.3)",
                                    "&:hover": {
                                        background: "linear-gradient(135deg, #003A75, #3A76B8)",
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 6px 20px rgba(0, 67, 142, 0.4)",
                                    },
                                }}
                            >
                                <FormattedMessage id="preliminaryResults.generate_btn" defaultMessage="Generate Preliminary Report" />
                            </Button>
                        </Box>
                    )}
                </DialogContent>

                {jobResult && (
                    <DialogActions sx={{ p: 3, pt: 0 }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                borderColor: "#00438E",
                                color: "#00438E",
                                borderRadius: "12px",
                                px: 3,
                                py: 1,
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": {
                                    borderColor: "#003A75",
                                    backgroundColor: "rgba(0, 67, 142, 0.05)",
                                },
                            }}
                        >
                            <FormattedMessage id="home.close" defaultMessage="Close" />
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            {/* Confirmation dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                sx={{
                    "& .MuiDialog-paper": {
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        background: "linear-gradient(135deg, #00438E 0%, #4A87C8 100%)",
                        color: "white",
                        px: 4,
                        py: 3,
                        position: "relative",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: "linear-gradient(90deg, #FFD700, #FFA000)",
                        },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Assessment sx={{ fontSize: "2rem" }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            <FormattedMessage id="preliminaryResults.confirm_title" defaultMessage="Generate Report" />
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 4, pt: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <FormattedMessage
                            id="preliminaryResults.confirm_body"
                            defaultMessage="Are you sure you want to generate a preliminary report for this assessment?"
                        />
                    </Typography>
                    <Box
                        sx={{
                            p: 2,
                            background: "rgba(0, 67, 142, 0.05)",
                            borderRadius: "8px",
                            border: "1px solid rgba(0, 67, 142, 0.1)",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            <FormattedMessage
                                id="preliminaryResults.confirm_note"
                                defaultMessage="This will trigger the analysis pipeline based on the current responses."
                            />
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 2 }}>
                    <Button
                        onClick={() => setConfirmDialogOpen(false)}
                        variant="outlined"
                        sx={{
                            borderColor: "#00438E",
                            color: "#00438E",
                            borderRadius: "12px",
                            px: 3,
                            py: 1,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": {
                                borderColor: "#003A75",
                                backgroundColor: "rgba(0, 67, 142, 0.05)",
                            },
                        }}
                    >
                        <FormattedMessage id="home.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button
                        onClick={handleGenerateReport}
                        variant="contained"
                        sx={{
                            background: "linear-gradient(135deg, #00438E, #4A87C8)",
                            color: "white",
                            borderRadius: "12px",
                            px: 3,
                            py: 1,
                            textTransform: "none",
                            fontWeight: 600,
                            boxShadow: "0 4px 16px rgba(0, 67, 142, 0.3)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #003A75, #3A76B8)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 6px 20px rgba(0, 67, 142, 0.4)",
                            },
                        }}
                    >
                        <FormattedMessage id="preliminaryResults.confirm_generate" defaultMessage="Confirm & Generate" />
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PreliminaryResultsModal;
